import { BehaviorSubject } from 'rxjs';
import { AvailabilityKind, Currency as CurrencyCode, SellChannel, TermStatus } from '../common/enum';
import { ServiceInterface } from '@/reservation-form/reservation-form.model';

export interface Selected {
	departure: SelectedDeparture;
}

export interface SelectedDeparture {
	kind: string;
	places: number[];
}

export interface MockData {
	header: string;
	terms: unknown;
	participants: Participants;
	roomType: Rooms;
	views: Views;
	promotions: Promotions;
	foods: Foods;
	departures: Departures;
	active: string;
}

export interface Values {
	naturalKey: string;
	name: string;
	active: boolean;
}

export interface Participant {
	id: number;
	name: string;
	quantity: number;
}

export interface Participants {
	participant: Participant[];
}

export interface Room {
	id: number;
	name: string;
}

export interface Rooms {
	room: Room[];
}

export interface View {
	id: number;
	name: string;
}

export interface Views {
	view: View[];
}

export interface Promotion {
	id: number;
	name: string;
	kind: string;
	value: string;
}

export interface Promotions {
	promotion: Promotion[];
}

export interface Food {
	id: number;
	name: string;
	isChecked: boolean;
}

export interface Foods {
	food: Food[];
}

export interface Departure {
	id: number;
	name: string;
	isChecked: boolean;
}

export interface Departures {
	departure: Departure[];
}

export interface DescriptionHeader {
	firstWord: string;
	id: number;
	multimedia: Multimedia;
	name: string;
	restHeader: string;
}

export interface Descriptions {
	description: string;
	descriptionHeader: DescriptionHeader;
	order: number;
	show: boolean;
	use?: TransportKind.COUCH | TransportKind.PLANE;
}

export interface FilterDefaultData {
	id: number | null;
	name: string;
	disable: boolean;
	availableCount?: number;
}

export interface FilterCheckboxData {
	id: number | null;
	name: string;
	isChecked?: boolean;
	disable?: boolean;
}

export interface Filters {
	adults: number[];
	boards: FilterCheckboxData[];
	children: number[];
	conveniences: FilterDefaultData[];
	count: number;
	ownTransport: boolean | string;
	kinds: FilterDefaultData[];
	locations: FilterDefaultData[];
	nights: number[];
	products: FilterDefaultData[];
	promotionCatalogs: FilterDefaultData[];
	roomType: FilterDefaultData[];
	stops: FilterCheckboxData[];
}

export interface Terms {
	id: number;
	board: { id: number; name: string };
	product: number;
	nightStay: number;
	dateRange: DateRange;
	fullPrice: number;
	transportType: string;
	catalogPrice: number;
	returnDatetime: string;
	departureDatetime: string;
	stop: { id: number; name: string };
	roomType: { id: number; name: string };
	promotion: { id: number; name: string; catalog: Catalog };
}

export interface DateRange {
	lower: string;
	upper: string;
	bounds: string;
}

export interface Schedule {
	id: number;
	stop: { id: number; stop: string; address: string };
	departureDatetime: string;
	returnDatetime: string;
	departureId: number;
	returnId: number;
	fee: number;
}

export interface FormatDate {
	date: string;
	time: string;
}

export interface SelectedNumber {
	adults: BehaviorSubject<number>;
	children: BehaviorSubject<number>;
	nights_stay_min: BehaviorSubject<number>;
	nights_stay_max: BehaviorSubject<number>;
}

export interface MobileConfigData {
	participant: string;
	term: string;
	roomType: string;
	boards: string;
	promotion: string;
	stops: string;
}

export interface LocationDescriptions {
	id: number;
	name: string;
	code: string;
	codeMerlinx: string;
	locationKind: string;
	parentLocation: number;
	locationDescriptions: LocationDescription[];
	locationOptionalTrips: LocationOptionalTrip[];
	multimedia: Multimedum[];
}

export interface LocationDescription {
	id: number;
	descriptionHeader: LocationDescriptionHeader;
	description: string;
}

export interface LocationDescriptionHeader {
	id: number;
	name: string;
	header: string;
	multimedia: Multimedia;
	comment: string;
}

export interface LocationOptionalTrip {
	id: number;
	optionalTrip: OptionalTrip;
	order: number;
}

export interface OptionalTrip {
	id: number;
	description: string;
	isActive: boolean;
	name: string;
}

export interface Multimedum {
	id: number;
	multimedia: MultimediaWithOrder;
	order: number;
}

export interface Data {
	board: Board;
	stop: Stop;
	room: Room;
	fullPrice: number;
	catalogPrice: number;
	promotion: Promotion;
	payments: Payment[];
}

export interface Board {
	board: number;
	name: string;
	boardPrice: number;
	currency: string;
	priceSetting: PriceSetting;
}

export interface PriceSetting {
	mainPrice: number;
	mainBoard: MainBoard;
	services: Service[];
}

export interface MainBoard {
	id: number;
	name: string;
}

export interface Service {
	optionName: string;
	value: number;
	countingMethod: string;
}

export interface Stop {
	transport: Transport;
	name: string;
	address: string;
	days: number;
	stop: number;
	departureDatetime: string;
	returnDatetime: string;
	fee: number;
	kind: string;
	amount: number;
	amountFree: number;
}

export enum TransportKind {
	COUCH = 'C',
	PLANE = 'F',
	OWN_TRANSPORT = 'O',
}

export const transportKindMap: Record<TransportKind, string> = {
	[TransportKind.COUCH]: 'Autobus',
	[TransportKind.PLANE]: 'Samolot',
	[TransportKind.OWN_TRANSPORT]: 'Dojazd własny',
};

export interface Transport {
	departureId: number;
	returnId: number;
	stopName: string;
	stopAddress: string;
	arrivalTime: string;
	departureTime: string;
}

export interface Room {
	term: Term;
	roomType: RoomType;
	fee: number;
	adults: number;
	children: number;
}

export interface Term {
	termId: number;
	product: Product;
	dateRange: DateRange;
	sale: boolean;
	www: boolean;
	merlinx: boolean;
	promotion: boolean;
	nightsStay: number;
	status: TermStatus;
}

export interface Product {
	id: number;
	name: string;
	code: string;
	productKind: ProductKind;
	organization: number;
	locations: Location[];
	multimedias: MultimediaWithOrder[];
	descriptions: Description[];
	surchargeBoard: SurchargeBoard;
	surchargeProgram: SurchargeProgram;
	conveniences: Convenience[];
	cities: Location[];
	tags: Tag[];
}

export interface ProductKind {
	id: number;
	name: string;
}

export interface Location {
	id: number;
	name: string;
	kind: string;
	parentLocation: Location | null;
}

export type City = Location;

export interface MultimediaWithOrder {
	multimedia: Multimedia;
	order: number;
}

export interface Multimedia {
	id: string;
	name: string;
	url: string;
}

export interface Description {
	descriptionHeader: DescriptionHeader;
	description: string;
}

export interface DescriptionHeader {
	id: number;
	name: string;
	multimedia: Multimedia;
}

export interface SurchargeBoard {
	id: number;
	fee: number;
	board: number;
	currency: Currency;
}

export interface Currency {
	id: number;
	name: string;
	code: CurrencyCode;
	exchangeRatePln: number;
}

export interface SurchargeProgram {
	id: number;
	description: string;
}

export interface Convenience {
	id: number;
	name: string;
	convenience: string;
	multimedia: Multimedia;
}

export interface Tag {
	id: number;
	name: string;
	tag: string;
}

export interface RoomType {
	id: number;
	name: string;
	code: string;
}

export interface Promotion {
	id: number;
	name: string;
	catalog: Catalog;
	description: string;
	method: string;
	methodValue: number;
	promotionOffers?: PromotionOffer[];
	sellChannels?: SellChannel[];
}

export interface PromotionOffer {
	offer: PromotionOfferDateRange;
}

export interface PromotionOfferDateRange {
	lower: string;
	upper: string;
	bounds: string;
}

export interface Catalog {
	id: number;
	name: string;
	multimedia: string;
}

export interface Payment {
	value: number;
	percentage: string;
	date: string;
}

export interface SurchargeToRoom {
	price: number;
	surcharge: number;
}

export interface AddedRoom {
	id: number;
	name: string;
	adultsNumber: number;
	boards: number[];
	kidsNumber: number;
	roomType: number[];
	stops: number[];
	ownTransport: boolean | string;
	surcharge: SurchargeToRoom;
	childrenBirthdates: string[];
	adultPrice: number;
	childrenPrice: number;
	priceTransportDeparture: number;
	priceTransportReturn: number;
	wasTransportAvailable: boolean;
}

export interface AvailableRoom {
	roomType: FilterDefaultData;
	availableCount?: number;
}

export interface OptimizedImageOptions {
	key: string;
	edits: {
		webp: {
			quality?: number;
			lossless?: boolean;
			smartSubsample?: boolean;
		};
		resize?: {
			width?: number;
			height?: number;
			fit?: string;
		};
		toFormat: string;
	};
}

export interface ConfigValue {
	id: number;
	name: string;
	multimedia?: string;
	disable?: boolean;
	availableCount?: number;
}

export interface AvailabilityCheck {
	available: boolean;
	type: AvailabilityKind;
}

export interface MenuMultimedia {
	id: string;
	name: string;
	url: string;
}

export type OfferService = ServiceInterface;
